import html2pdf from 'html2pdf.js';
import { fetchSocieteInfos, getSocieteInfos } from "./societe";

import { formatCurrency, formatNumber, nettoyerMontant } from "../utils/formatters";
import logo1 from "../assets/img/IMG-20250130-WA0008.jpg";

/**
 * Génère un PDF pour le rapport CA avec les données réelles de l'API
 */
export const generateCAPDF = async (data, selectedYear) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Récupérer les informations société
      let societe = await fetchSocieteInfos();
      if (!societe || Object.keys(societe).length === 0) {
        societe = getSocieteInfos();
      }
      
      const mois = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
      
      // Calcul des totaux à partir des données réelles
      const caAnnuel = data?.annuel?.reduce((acc, m) => acc + (parseFloat(m.total_ttc) || 0), 0) || 0;
      const caMensuelMoyen = caAnnuel / 12;
      const totalTVA = data?.annuel?.reduce((acc, m) => acc + (parseFloat(m.total_tva) || 0), 0) || 0;
      const totalRegle = data?.annuel?.reduce((acc, m) => acc + (parseFloat(m.total_regle) || 0), 0) || 0;
      const tauxEncaissement = caAnnuel > 0 ? (totalRegle / caAnnuel) * 100 : 0;
      const nbClientsUniques = data?.top_clients?.length || 0;
      
      // Générer le HTML du tableau mensuel avec les données réelles
      const generateMonthlyTable = () => {
        if (!data?.annuel || data.annuel.length === 0) {
          return '<p style="text-align: center; padding: 20px;">Aucune donnée disponible</p>';
        }
        
        let html = '<table style="width: 100%; border-collapse: collapse;">';
        html += `<thead><tr>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: left;">Mois</th>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: right;">Factures</th>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: right;">Clients</th>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: right;">HT (FCFA)</th>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: right;">TVA (FCFA)</th>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: right;">TTC (FCFA)</th>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: right;">Réglé (FCFA)</th>
        </tr></thead>`;
        html += '<tbody>';
        
        data.annuel.forEach((item, index) => {
          const bgColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
          html += `<tr style="background: ${bgColor};">`;
          html += `<td style="padding: 10px; font-weight: 600;">${mois[item.mois - 1] || item.mois}</td>`;
          html += `<td style="padding: 10px; text-align: right;">${formatNumber(item.nb_factures || 0)}</td>`;
          html += `<td style="padding: 10px; text-align: right;">${formatNumber(item.nb_clients || 0)}</td>`;
          html += `<td style="padding: 10px; text-align: right;">${formatCurrency(item.total_ht || 0)}</td>`;
          html += `<td style="padding: 10px; text-align: right;">${formatCurrency(item.total_tva || 0)}</td>`;
          html += `<td style="padding: 10px; text-align: right; font-weight: bold; color: #46A93C;">${formatCurrency(item.total_ttc || 0)}</td>`;
          html += `<td style="padding: 10px; text-align: right; color: #1EADCF;">${formatCurrency(item.total_regle || 0)}</td>`;
          html += `</tr>`;
        });
        
        // Ligne de total
        html += `<tr style="background: #e9ecef; font-weight: bold;">
          <td style="padding: 10px;">TOTAL</td>
          <td style="padding: 10px; text-align: right;">${formatNumber(data.annuel.reduce((acc, m) => acc + (m.nb_factures || 0), 0))}</td>
          <td style="padding: 10px; text-align: right;">-</td>
          <td style="padding: 10px; text-align: right;">${formatCurrency(data.annuel.reduce((acc, m) => acc + (parseFloat(m.total_ht) || 0), 0))}</td>
          <td style="padding: 10px; text-align: right;">${formatCurrency(totalTVA)}</td>
          <td style="padding: 10px; text-align: right; color: #46A93C;">${formatCurrency(caAnnuel)}</td>
          <td style="padding: 10px; text-align: right; color: #1EADCF;">${formatCurrency(totalRegle)}</td>
        <tr>`;
        
        html += '</tbody></table>';
        return html;
      };
      
      // Générer le HTML des top clients
      const generateTopClientsTable = () => {
        if (!data?.top_clients || data.top_clients.length === 0) {
          return '<p style="text-align: center; padding: 20px;">Aucun client</p>';
        }
        
        let html = '<table style="width: 100%; border-collapse: collapse;">';
        html += `<thead><tr>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: left;">Client</th>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: center;">Code</th>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: center;">Factures</th>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: right;">CA TTC (FCFA)</th>
        <tr></thead>`;
        html += '<tbody>';
        
        data.top_clients.slice(0, 5).forEach((client, index) => {
          const bgColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
          html += `<tr style="background: ${bgColor};">`;
          html += `<td style="padding: 10px; font-weight: 600;">${client.raison_sociale || '-'}</td>`;
          html += `<td style="padding: 10px; text-align: center; color: #6c757d;">${client.code_client || '-'}</td>`;
          html += `<td style="padding: 10px; text-align: center;">${formatNumber(client.nb_factures || 0)}</td>`;
          html += `<td style="padding: 10px; text-align: right; font-weight: bold; color: #46A93C;">${formatCurrency(client.total_ttc || 0)}</td>`;
          html += `</tr>`;
        });
        
        html += '</tbody></table>';
        return html;
      };
      
      // Générer le HTML des top produits
      const generateTopProductsTable = () => {
        if (!data?.top_produits || data.top_produits.length === 0) {
          return '<p style="text-align: center; padding: 20px;">Aucun produit</p>';
        }
        
        let html = '<table style="width: 100%; border-collapse: collapse;">';
        html += `<thead><tr>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: left;">Produit</th>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: center;">Référence</th>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: center;">Quantité</th>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: right;">Montant (FCFA)</th>
        <tr></thead>`;
        html += '<tbody>';
        
        data.top_produits.slice(0, 5).forEach((produit, index) => {
          const bgColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
          html += `<tr style="background: ${bgColor};">`;
          html += `<td style="padding: 10px;">${produit.designation || '-'}</td>`;
          html += `<td style="padding: 10px; text-align: center; color: #6c757d;">${produit.reference || '-'}</td>`;
          html += `<td style="padding: 10px; text-align: center;">${formatNumber(produit.quantite_vendue || 0)}</td>`;
          html += `<td style="padding: 10px; text-align: right; font-weight: bold; color: #46A93C;">${formatCurrency(produit.montant_total || 0)}</td>`;
          html += `</tr>`;
        });
        
        html += '</tbody></table>';
        return html;
      };
      
      // Générer le HTML des catégories
      const generateCategoriesTable = () => {
        if (!data?.ventes_categories || data.ventes_categories.length === 0) {
          return '<p style="text-align: center; padding: 20px;">Aucune catégorie</p>';
        }
        
        let html = '<table style="width: 100%; border-collapse: collapse;">';
        html += `<thead><tr>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: left;">Catégorie</th>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: right;">Montant (FCFA)</th>
          <th style="background: #46A93C; color: white; padding: 10px; text-align: center;">Part du CA</th>
        </tr></thead>`;
        html += '<tbody>';
        
        data.ventes_categories.forEach((cat, index) => {
          const percentage = ((parseFloat(cat.montant_total) / (caAnnuel || 1)) * 100).toFixed(1);
          const bgColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
          html += `<tr style="background: ${bgColor};">`;
          html += `<td style="padding: 10px;">${cat.nom_categorie || 'Sans catégorie'}</td>`;
          html += `<td style="padding: 10px; text-align: right;">${formatCurrency(cat.montant_total || 0)}</td>`;
          html += `<td style="padding: 10px; text-align: center;"><span style="background: ${percentage > 20 ? '#d4edda' : '#fff3cd'}; color: ${percentage > 20 ? '#155724' : '#856404'}; padding: 3px 10px; border-radius: 4px; font-size: 9px; font-weight: 600;">${percentage}%</span></td>`;
          html += `</tr>`;
        });
        
        html += '</tbody></table>';
        return html;
      };
      
      // Convertir le logo en base64
      const getLogoBase64 = () => {
        return new Promise((resolveLogo) => {
          fetch(logo1)
            .then(response => response.blob())
            .then(blob => {
              const reader = new FileReader();
              reader.onloadend = () => resolveLogo(reader.result);
              reader.readAsDataURL(blob);
            })
            .catch(() => resolveLogo(''));
        });
      };
      
      // Générer le HTML complet
      const generateHTMLWithLogo = (logoBase64) => {
        return `<!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Rapport Financier ${selectedYear}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', Arial, sans-serif; 
              background: #f0f2f5; 
              padding: 20px; 
              font-size: 11px; 
              color: #2c3e50;
            }
            .report-container { 
              max-width: 1200px; 
              margin: 0 auto; 
              background: white; 
              overflow: hidden;
              box-shadow: 0 5px 20px rgba(0,0,0,0.1);
            }
            
            .header { 
              padding: 20px 25px;
              
              background: white;
            }
            .header-top { display: flex; justify-content: space-between; align-items: center; }
            .logo-img { height: 70px; width: auto; border-radius: 0; }
            .company-section { text-align: right; }
            .company-name { font-size: 16px; font-weight: bold; color: #2c3e50; margin-bottom: 5px; }
            .company-info { font-size: 9px; color: #6c757d; margin-bottom: 3px; }
            
            .title-section { 
              text-align: center; 
              margin: 20px 30px; 
              padding-bottom: 15px; 
              border-bottom: 3px solid #46A93C;
            }
            .title-section h1 { font-size: 22px; font-weight: bold; color: #46A93C; margin-bottom: 5px; }
            .title-section p { font-size: 11px; color: #6c757d; }
            .date { font-size: 9px; color: #adb5bd; margin-top: 5px; }
            
            .stats-grid { 
              display: flex; 
              gap: 15px; 
              margin: 20px 30px; 
              flex-wrap: wrap; 
            }
            .stat-card { 
              flex: 1; 
              background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
              padding: 15px; 
              border-radius: 10px; 
              text-align: center;
              border-left: 4px solid #46A93C;
            }
            .stat-card h4 { font-size: 10px; color: #6c757d; margin-bottom: 8px; text-transform: uppercase; }
            .stat-card .value { font-size: 16px; font-weight: bold; color: #46A93C; }
            .stat-card .unit { font-size: 9px; color: #6c757d; }
            
            .section { margin: 25px 30px; }
            .section-title { 
              font-size: 14px; 
              font-weight: bold; 
              color: #1a3c5e; 
              border-left: 4px solid #46A93C; 
              padding-left: 12px; 
              margin-bottom: 15px;
            }
            
            table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            th { background: #f8f9fa; color: #2c3e50; padding: 10px; border-bottom: 2px solid #46A93C; }
            td { padding: 8px 10px; border-bottom: 1px solid #e0e0e0; }
            tr:nth-child(even) { background: #fafbfc; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            
            .footer { 
              margin-top: 30px; 
              padding: 15px 30px; 
              background: #f8f9fa;
              text-align: center; 
              font-size: 8px; 
              color: #6c757d;
              border-top: 1px solid #e0e0e0;
            }
            
            @media print {
              body { background: white; padding: 0; }
              .report-container { box-shadow: none; }
              th { background: #f8f9fa !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="report-container">
            <div class="header">
              <div class="header-top">
                <div class="logo-section">
                  ${logoBase64 ? `<img src="${logoBase64}" alt="Logo" class="logo-img" />` : '<div style="width: 70px;"></div>'}
                </div>
                <div class="company-section">
                  <div class="company-name">${societe.nom || 'GEOTECH'}</div>
                  <div class="company-info">${societe.adresse || ''}</div>
                  <div class="company-info">Tél: ${societe.telephone || ''} | Email: ${societe.email || ''}</div>
                  <div class="company-info">RC: ${societe.rc || ''} | NIF: ${societe.nif || ''}</div>
                </div>
              </div>
            </div>
            <br>
            <div class="title-section">
              <h1>RAPPORT FINANCIER ${selectedYear}</h1>
              <p>Analyse des performances commerciales</p>
              <div class="date">Généré le ${new Date().toLocaleDateString('fr-FR')}</div>
            </div>
            
            <div class="stats-grid">
              <div class="stat-card"><h4>CA Annuel</h4><div class="value">${formatCurrency(caAnnuel)}</div><div class="unit">FCFA</div></div>
              <div class="stat-card"><h4>CA Mensuel moyen</h4><div class="value">${formatCurrency(caMensuelMoyen)}</div><div class="unit">FCFA</div></div>
              <div class="stat-card"><h4>Clients actifs</h4><div class="value">${formatNumber(nbClientsUniques)}</div><div class="unit">clients</div></div>
              <div class="stat-card"><h4>Taux encaissement</h4><div class="value">${tauxEncaissement.toFixed(1)}%</div><div class="unit">du CA</div></div>
            </div>
            
            <div class="section">
              <div class="section-title"> Évolution mensuelle du CA</div>
              ${generateMonthlyTable()}
            </div>
            
            <div class="section">
              <div class="section-title"> Top 5 Clients</div>
              ${generateTopClientsTable()}
            </div>
            
           
            <div class="footer">
              <div>${societe.nom || 'GEOTECH'} • Système de gestion commerciale</div>
              <div style="margin-top: 5px;">Document généré automatiquement - Rapport confidentiel</div>
            </div>
          </div>
        </body>
        </html>`;
      };
      
      const logoBase64 = await getLogoBase64();
      const htmlContent = generateHTMLWithLogo(logoBase64);
      
      const options = {
        margin: [8, 8, 8, 8],
        filename: `Rapport_CA_${selectedYear}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, letterRendering: true, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      
      const element = document.createElement('div');
      element.innerHTML = htmlContent;
      document.body.appendChild(element);
      
      html2pdf().set(options).from(element).save().then(() => {
        document.body.removeChild(element);
        resolve(true);
      }).catch((error) => {
        document.body.removeChild(element);
        reject(error);
      });
      
    } catch (error) {
      console.error("Erreur génération PDF:", error);
      reject(error);
    }
  });
};